/**
 * Section Selector Component
 *
 * Visual selector for marking sections for copy operations
 * Three states:
 * 1. Default: Hollow circle
 * 2. Hover: Hollow circle with plus icon
 * 3. Selected: Filled purple circle with checkmark
 */

import React, { useState } from 'react';
import { Add as AddIcon, Check as CheckIcon } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export interface SectionSelectorProps {
  /** Section ID */
  sectionId: string;
  /** Whether this section is selected */
  isSelected: boolean;
  /** Callback when selection is toggled */
  onToggle: (sectionId: string) => void;
  /** Optional size in pixels */
  size?: number;
}

/**
 * Section selector with three visual states
 */
export const SectionSelector: React.FC<SectionSelectorProps> = ({
  sectionId,
  isSelected,
  onToggle,
  size = 20,
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent click handlers
    onToggle(sectionId);
  };

  // Selected state: Filled purple circle with white checkmark
  if (isSelected) {
    return (
      <Box
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          width: size,
          height: size,
          minWidth: size,
          minHeight: size,
          flexShrink: 0,
          borderRadius: '50%',
          bgcolor: theme.palette.primary.main,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: theme.transitions.create(['background-color', 'transform'], {
            duration: theme.transitions.duration.shortest,
          }),
          '&:hover': {
            bgcolor: '#2a1a7d', // Custom hover color not in theme
            transform: 'scale(1.1)',
          },
        }}
      >
        <CheckIcon
          sx={{
            fontSize: size * 0.7,
            color: theme.palette.common.white,
          }}
        />
      </Box>
    );
  }

  // Default/Hover state: Hollow circle with optional plus icon
  return (
    <Box
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        flexShrink: 0,
        borderRadius: '50%',
        border: `2px solid ${isHovered ? theme.palette.primary.main : '#E0E0E0'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: theme.transitions.create(['border-color', 'transform'], {
          duration: theme.transitions.duration.shortest,
        }),
        '&:hover': {
          borderColor: theme.palette.primary.main,
          transform: 'scale(1.1)',
        },
      }}
    >
      {isHovered && (
        <AddIcon
          sx={{
            fontSize: size * 0.7,
            color: theme.palette.primary.main,
          }}
        />
      )}
    </Box>
  );
};
